// https://babeljs.io/blog/2019/03/19/7.4.0#migration-from-core-js-2
import 'core-js/stable';
import 'regenerator-runtime/runtime';

// should be imported before other code to run first

import React from 'react';
import * as ReactDOM from 'react-dom';
import Cookies from 'js-cookie';
import { loadableReady } from '@loadable/component';
import { ReduxAsyncConnect } from 'ha/modules/ReduxConnect';
import {
  applyRouterMiddleware,
  browserHistory,
  match,
  Router,
} from 'react-router';

import { createIntl } from 'ha/i18n';
import getAppServices from 'ha/services/getAppServices';

import AppContextProvider from 'ha/myredux/AppContextProvider';

import createAppStore from 'ha/myredux/createAppStore';
import getInitialState from 'ha/myredux/getInitialState';

import reportError from 'ha/helpers/bugsnag/reportError';
import isIgnoredHosts from 'ha/utils/isIgnoredHosts';

import CookiesConstants from 'ha/constants/Cookies';
import initSession from 'ha/modules/AuthLogic/actions/initSession';
import { setupMediaQueryListeners } from 'ha/modules/UI/actions';
import { getUserId } from 'ha/modules/AuthLogic/selectors';
import getLanguageFromUrl from 'ha/utils/getLanguageFromUrl';
import { track, initAnalytics } from 'ha/modules/Analytics/actions';
import { newReduxTracker as newAffiliationTracker } from 'ha/modules/Affiliation';
import handleInstallPrompt from 'ha/serviceWorker/utils/handleInstallPrompt';
import initServiceWorker from 'ha/serviceWorker/utils/initServiceWorker';
import getHaSwUrl from 'ha/serviceWorker/utils/getHaSwUrl';
import algoliaService from 'ha/services/algolia';
import SplitIOClient from 'ha/services/SplitIO';
import LocalyseService from 'ha/services/localyse';

import FetchClient from 'ha/helpers/FetchClient';
import { config } from './setup';

import getRoutes from './getRoutes';

if (global.Bugsnag) {
  global.Bugsnag.app.version = `${__VERSION__}__${__COMMITHASH__}`;
}

/* eslint-disable no-underscore-dangle */
const preloadedState = global.__PRELOADED_STATE__ || {};
/* eslint-enable no-underscore-dangle */

const rootEl = document.getElementById('app-root');

const lang = getLanguageFromUrl(window.location.pathname);
const intl = createIntl({
  lang,
  messages: global.Translations,
  baseDomain: config.baseDomain,
});

global.baseUrl = config.baseDomain;

const fetchOptions = {
  headers: {
    'X-HA-Lang': lang,
  },
};

const haApi = new FetchClient(config.backend.host, fetchOptions);

const initSplitClient = (userKey: string) =>
  import(/* webpackChunkName: "splitio" */ '@splitsoftware/splitio').then(
    ({ SplitFactory }) =>
      SplitFactory({
        ...config.split,
        core: {
          ...config.split.core,
          key: userKey,
        },
      }).client(),
  );

const getAlgoliaClient = () =>
  import(/* webpackChunkName: "algoliasearch" */ 'ha/helpers/algoliaWrapper')
    .then(module => module.default)
    .then(algoliaSDK =>
      algoliaSDK(config.algolia.appId, config.algolia.apiKey),
    );

const algolia = algoliaService({
  initializer: getAlgoliaClient,
  index: config.algolia.index,
});

const services = getAppServices({
  config,
  clients: {
    algolia,
    haApi,
    moneta: new FetchClient(config.moneta.host),
    osiris: new FetchClient(config.osiris.host),
    metis: new FetchClient(config.metis.host, {
      credentials: 'omit',
    }),
    hermes: new FetchClient(config.hermes.host, {
      credentials: 'omit',
    }),
    almanac: new FetchClient(config.almanac.host, {
      credentials: 'omit',
    }),
    bell: new FetchClient(config.bell.host),
    cookie: Cookies,
    splitio: new SplitIOClient({
      initializer: initSplitClient,
      isClient: true,
    }),
    localyse: new LocalyseService(config.localyse.host, config.localyse.apiKey),
  },
  lang,
});

const store = createAppStore({
  config,
  errorTracker: global.Bugsnag,
  history: browserHistory,
  initialState: getInitialState({
    preloadedState,
    language: lang,
  }),
  services,
});

window.addEventListener(
  'beforeinstallprompt',
  handleInstallPrompt({
    onChoice: choiceResult =>
      store.dispatch(
        track('Web App Install Banner', { action: choiceResult.outcome }),
      ),
    onError: reportError,
  }),
);

Object.defineProperty(global, 'reduxStore', {
  get: () => store,
  set: () => {
    throw new Error('Trying to modify `window.reduxStore` property!');
  },
});

Promise.all([
  new Promise((resolve: any) => loadableReady(resolve)),
  store.dispatch(initSession(lang)),
])
  .catch(reportError)
  .then(() => {
    const userId = getUserId(store.getState());

    if (global.Bugsnag && userId) {
      global.Bugsnag.user = {
        id: userId,
      };
    }
  })
  .then(
    () =>
      new Promise(resolve => {
        if (isIgnoredHosts(global.location.host)) {
          // @ts-ignore
          resolve();
        } else {
          const routes = getRoutes(store, intl.urlResolver);
          const useReduxConnect = () => ({
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            renderRouterContext: (child: React.ReactNode, props: any) => (
              <ReduxAsyncConnect {...props} intl={intl}>
                {child}
              </ReduxAsyncConnect>
            ),
          });

          match(
            {
              routes,
              history: browserHistory,
            },
            (error, redirectLocation, renderProps) => {
              ReactDOM.hydrate(
                // @ts-ignore
                <AppContextProvider
                  config={config}
                  intl={intl}
                  store={store}
                  services={services}
                >
                  <Router
                    history={browserHistory}
                    routes={routes}
                    {...renderProps}
                    render={applyRouterMiddleware(useReduxConnect())}
                  />
                </AppContextProvider>,
                rootEl,
                resolve,
              );
            },
          );
        }
      }),
  )
  .then(() => {
    store.dispatch(setupMediaQueryListeners());
  })
  .then(() => {
    if ('serviceWorker' in global.navigator && config.webApp.enableSW) {
      initServiceWorker(
        getHaSwUrl(
          config.baseDomain,
          Cookies.getJSON(CookiesConstants.PWA_NEXT_ENABLED) as number,
        ),
        {
          origin: global.location.origin,
          onError: reportError,
        },
      );
    }
  })
  .then(() => {
    newAffiliationTracker(services.cookie).trackSource(
      browserHistory.getCurrentLocation(),
    );
    store.dispatch(
      initAnalytics({
        config,
        location: browserHistory.getCurrentLocation(),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        referrer: (global as any).document.referrer,
        setCookie: services.cookie.setCookie,
      }),
    );
  })
  .catch(reportError)
  .then(() => {
    global.appReady = true;
  });
